import type { ComponentProps } from 'react';
import { useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { de, enUS as en } from 'date-fns/locale';

import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { CALENDAR_BASE, mergeCalendarClassNames } from './calendar.styles';

type DayPickerCN = Partial<NonNullable<ComponentProps<typeof DayPicker>['classNames']>>;
type CalendarProps = ComponentProps<typeof DayPicker> & {
  presetClassNames?: DayPickerCN;
  fixedRowCount?: 5;
};

const localesMap = {
  en,
  de,
};

export const Calendar = ({
  className,
  classNames,
  presetClassNames,
  showOutsideDays = true,
  fixedRowCount = 5,
  ...props
}: CalendarProps) => {
  const { i18n } = useTranslation();
  const locale = localesMap[i18n.language as keyof typeof localesMap];
  const defaultButtonClasses: DayPickerCN = {
    nav_button: cn(
      buttonVariants({ variant: 'outline' }),
      'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
    ),
    day: cn(
      buttonVariants({ variant: 'outline' }),
      'mx-[2px] h-8 w-8 p-0 font-normal aria-selected:opacity-100'
    ),
  };

  const dynamicStructural: DayPickerCN = {
    cell: cn(
      'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
      props.mode === 'range'
        ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
        : '[&:has([aria-selected])]:rounded-md'
    ),
  };

  const finalClassNames = mergeCalendarClassNames(
    defaultButtonClasses,
    dynamicStructural,
    CALENDAR_BASE,
    presetClassNames,
    classNames
  );
  const fixedRowsClass = fixedRowCount === 5 ? '[&_.rdp-row:nth-child(6)]:hidden' : undefined;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [rowCount, setRowCount] = useState<number>(5);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const update = () => {
      const rows = el.querySelectorAll('.rdp-row');
      setRowCount(rows.length || 5);
    };
    update();

    const observer = new MutationObserver(() => update());
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [locale, showOutsideDays, fixedRowCount]);

  const phantomRows = fixedRowCount && rowCount < fixedRowCount ? fixedRowCount - rowCount : 0;

  return (
    <div ref={rootRef} className={cn('p-3', className)}>
      <DayPicker
        locale={locale}
        showOutsideDays={showOutsideDays}
        className={cn(fixedRowsClass)}
        classNames={finalClassNames}
        components={{
          IconLeft: () => <ChevronLeftIcon className="size-4" />,
          IconRight: () => <ChevronRightIcon className="size-4" />,
        }}
        {...props}
      />
      {fixedRowCount === 5 && phantomRows > 0
        ? Array.from({ length: phantomRows }).map((_, idx) => (
            <div
              key={`phantom-row-${String.fromCharCode(65 + idx)}`}
              className="pointer-events-none mt-2 grid grid-cols-7 gap-x-1 gap-y-2 opacity-0"
              aria-hidden
            >
              {Array.from({ length: 7 }).map((__, i) => (
                <div key={`date-${String.fromCharCode(65 + i)}`} className="h-10 w-10" />
              ))}
            </div>
          ))
        : null}
    </div>
  );
};
